import { Fragment, useState, useEffect } from 'react'
import {
  Stack, Text, Box, Table, Badge, Alert, Tooltip, Group, ActionIcon, Button,
} from '@mantine/core'
import { IconAlertCircle, IconBell, IconChevronRight, IconChevronDown, IconDownload, IconFilterOff, IconShieldSearch } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import { useAuthStore, useCurrentTenant } from '@/store/auth'
import { DataCard, EmptyState } from '@/components/ui'
import { SeverityBadge, type Severity } from '@/components/audit/SeverityBadge'
import { OutcomeIcon, type Outcome } from '@/components/audit/OutcomeIcon'
import { ActorCell } from '@/components/audit/ActorCell'
import { FiltersToolbar, defaultFilters, type AuditFilters } from '@/components/audit/FiltersToolbar'
import { ExpandedRow } from '@/components/audit/ExpandedRow'
import { TenantMultiSelect } from '@/components/audit/TenantMultiSelect'

interface AuditEvent {
  id?: number
  tenant_id?: number    // presente em respostas cross-tenant (/v1/audit/global)
  action?: string
  event_type?: string
  resource_type?: string
  resource_id?: string | null
  details?: string | Record<string, unknown>
  payload?: Record<string, unknown>
  before_state?: Record<string, unknown> | null
  after_state?: Record<string, unknown> | null
  created_at?: string
  actor_user_email?: string
  actor_user_name?: string
  actor_roles?: string[]
  severity?: Severity
  outcome?: Outcome
  legacy?: boolean
  client_ip?: string | null
  user_agent?: string | null
  request_id?: string | null
  http_method?: string | null
  http_path?: string | null
  http_status?: number | null
  duration_ms?: number | null
  prev_event_hash?: string | null
  event_hash?: string | null
}

interface AuditListResponse {
  items: AuditEvent[]
  total: number
  tenant_ids?: number[]   // só presente na resposta cross-tenant
}

const PAGE_SIZE = 20

// Roles que disparam o modo cross-tenant em /auditoria (S2-pt1).
// Spec: docs/superpowers/specs/2026-05-29-audit-multitenant-view-design.md §4.5
const MULTI_TENANT_ROLES = new Set([
  'admin', 'devops', 'gestor', 'auditor_global',
])

function formatDate(value?: string): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('pt-BR')
}

function resourceTypeColor(type?: string): string {
  if (!type) return 'gray'
  const t = type.toLowerCase()
  if (t.includes('ec2') || t.includes('instance')) return 'blue'
  if (t.includes('user') || t.includes('auth')) return 'violet'
  if (t.includes('budget') || t.includes('cost')) return 'teal'
  return 'gray'
}

function buildQueryString(f: AuditFilters, offset: number): string {
  const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) })
  if (f.period_from) p.set('period_from', f.period_from.toISOString())
  if (f.period_to) p.set('period_to', f.period_to.toISOString())
  f.severity.forEach((s) => p.append('severity', s))
  f.outcome.forEach((o) => p.append('outcome', o))
  if (f.actor_email) p.set('actor_email', f.actor_email)
  if (f.action_glob) p.set('action_glob', f.action_glob)
  f.resource_type.forEach((r) => p.append('resource_type', r))
  if (f.client_ip) p.set('client_ip', f.client_ip)
  if (f.request_id) p.set('request_id', f.request_id)
  if (f.payload_search) p.set('payload_search', f.payload_search)
  return p.toString()
}

export function Auditoria() {
  const navigate = useNavigate()
  const tenantId = useCurrentTenant()?.id
  const user = useAuthStore((s) => s.user)
  const hasGlobalAudit = !!user?.roles?.some((r) => MULTI_TENANT_ROLES.has(r.code))

  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<AuditFilters>(defaultFilters())
  // null = "todos os tenants" (= sem filtro tenant_ids no request, backend
  // retorna todos os tenants permitidos)
  const [selectedTenantIds, setSelectedTenantIds] = useState<number[] | null>(null)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const offset = (page - 1) * PAGE_SIZE

  // Auto-expand via ?event_id=N (deep-link do sino)
  useEffect(() => {
    const url = new URL(window.location.href)
    const eid = url.searchParams.get('event_id')
    if (eid) setExpanded(new Set([Number(eid)]))
  }, [])

  const { data, isLoading, error, refetch, isFetching } = useQuery<AuditListResponse, Error>({
    queryKey: hasGlobalAudit
      ? ['audit', 'global', selectedTenantIds, offset, filters]
      : ['audit', tenantId, offset, filters],
    queryFn: async () => {
      const qs = buildQueryString(filters, offset)
      if (hasGlobalAudit) {
        // Cross-tenant: usa /v1/audit/global. tenant_ids serializado como
        // ?tenant_ids=1&tenant_ids=2 (URLSearchParams.append). null = omit.
        const params = new URLSearchParams(qs)
        if (selectedTenantIds) {
          selectedTenantIds.forEach((id) => params.append('tenant_ids', String(id)))
        }
        const res = await api.get<AuditListResponse>(`/v1/audit/global?${params.toString()}`)
        return res.data
      }
      const res = await api.get<AuditListResponse>(`/v1/tenants/${tenantId}/audit?${qs}`)
      return res.data
    },
    // Cross-tenant nao depende de tenant ativo no sidebar; per-tenant sim.
    enabled: hasGlobalAudit || !!tenantId,
    retry: false,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0

  const toggleExpand = (id?: number) => {
    if (id == null) return
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleExport = (format: 'csv' | 'json') => {
    const qs = buildQueryString(filters, 0).replace(/limit=\d+/, 'limit=50000').replace(/offset=\d+/, 'offset=0')
    window.open(`/api/v1/tenants/${tenantId}/audit/export?format=${format}&${qs}`, '_blank')
  }

  return (
    <Stack gap="xl">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Não foi possível carregar os eventos. Tente novamente em instantes.
        </Alert>
      )}

      <FiltersToolbar
        value={filters}
        onApply={(f) => { setFilters(f); setPage(1) }}
        tenantSelector={hasGlobalAudit ? (
          <TenantMultiSelect
            value={selectedTenantIds}
            onChange={(ids) => { setSelectedTenantIds(ids); setPage(1) }}
          />
        ) : undefined}
      />

      <DataCard flush
        title="Eventos de Auditoria"
        accent="info"
        badge={{ value: total, color: 'blue' }}
        loading={isLoading}
        refreshable refreshing={isFetching} onRefresh={() => refetch()}
        collapsible fullscreenable
        pagination={{ page, total, pageSize: PAGE_SIZE, onChange: setPage }}
        extra={
          <Group gap="xs">
            {/* Fase 4 (MR 3/5): AuditBell removido. Audit events agora
              aparecem no sino global do Header (unified feed). Link
              direto pra pagina de notificacoes filtrada por source=audit. */}
            <Button
              size="xs"
              variant="light"
              leftSection={<IconBell size={14} />}
              onClick={() => navigate('/notificacoes?source=audit')}
            >
              Ver no feed
            </Button>
            <Button
              size="xs" variant="light"
              leftSection={<IconDownload size={14} />}
              disabled={!tenantId}
              onClick={() => handleExport('csv')}
            >
              CSV
            </Button>
            <Button
              size="xs" variant="light"
              leftSection={<IconDownload size={14} />}
              disabled={!tenantId}
              onClick={() => handleExport('json')}
            >
              JSON
            </Button>
          </Group>
        }
      >
        {items.length === 0 ? (
          (() => {
            const hasActiveFilters =
              JSON.stringify(filters) !== JSON.stringify(defaultFilters())
            return hasActiveFilters ? (
              <EmptyState
                bare
                size="md"
                accent="info"
                icon={IconFilterOff}
                title="Nenhum evento corresponde aos filtros"
                description="Tente ampliar o período ou remover filtros de severidade, usuário ou ação."
                primaryAction={{
                  label: 'Limpar filtros',
                  icon: <IconFilterOff size={14} />,
                  onClick: () => { setFilters(defaultFilters()); setPage(1) },
                }}
              />
            ) : (
              <EmptyState
                bare
                size="md"
                accent="info"
                icon={IconShieldSearch}
                title="Sem eventos de auditoria registrados"
                description="Eventos de acesso, alteração e ações sensíveis serão listados aqui assim que ocorrerem."
              />
            )
          })()
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={32}></Table.Th>
                <Table.Th style={{ whiteSpace: 'nowrap' }}>Data</Table.Th>
                <Table.Th>Sev</Table.Th>
                {hasGlobalAudit && <Table.Th>Tenant</Table.Th>}
                <Table.Th>Usuário</Table.Th>
                <Table.Th>Ação</Table.Th>
                <Table.Th>Recurso</Table.Th>
                <Table.Th>Out</Table.Th>
                <Table.Th>Origem</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((event, i) => {
                const isExp = event.id != null && expanded.has(event.id)
                const rowKey = event.id ?? i
                return (
                  <Fragment key={rowKey}>
                    <Table.Tr>
                      <Table.Td>
                        <ActionIcon variant="subtle" size="sm" onClick={() => toggleExpand(event.id)}>
                          {isExp ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                        </ActionIcon>
                      </Table.Td>
                      <Table.Td><Text size="xs" ff="monospace">{formatDate(event.created_at)}</Text></Table.Td>
                      <Table.Td><SeverityBadge severity={event.severity} /></Table.Td>
                      {hasGlobalAudit && (
                        <Table.Td>
                          {event.tenant_id != null ? (
                            <Badge size="xs" variant="outline">{event.tenant_id}</Badge>
                          ) : <Text size="xs" c="dimmed">—</Text>}
                        </Table.Td>
                      )}
                      <Table.Td>
                        <ActorCell actor={{
                          email: event.actor_user_email, name: event.actor_user_name,
                          roles: event.actor_roles, legacy: event.legacy,
                        }} />
                      </Table.Td>
                      <Table.Td><Text size="sm" fw={500}>{event.action ?? event.event_type ?? '—'}</Text></Table.Td>
                      <Table.Td>
                        {event.resource_type ? (
                          <Badge size="sm" color={resourceTypeColor(event.resource_type)} variant="light">
                            {event.resource_type}
                          </Badge>
                        ) : <Text size="xs" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td><OutcomeIcon outcome={event.outcome} /></Table.Td>
                      <Table.Td>
                        <Tooltip label={`${event.client_ip ?? '?'}\nUA: ${event.user_agent ?? '?'}`} multiline>
                          <Text size="xs" ff="monospace">{event.client_ip ?? '—'}</Text>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                    {isExp && (
                      <Table.Tr>
                        <Table.Td colSpan={hasGlobalAudit ? 9 : 8} p={0}>
                          <ExpandedRow event={event} />
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Fragment>
                )
              })}
            </Table.Tbody>
          </Table>
        )}
      </DataCard>
    </Stack>
  )
}
